import Dialog from "../../../General/ConfirmableDialog";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import ConfirmActionButton from "../../ConfirmActionButton/ConfirmActionButton";

interface IPreviewGalleryModalProps {
  open: boolean;
  onClose: () => void;
  accident: any;
}

interface AccidentFile {
  id: string;
  accidentId: string;
  fileName: string;
}

interface AccidentAsset {
  url: string;
  contentType: string;
}

const loadAuthorizedFile = async (
  fileName: string,
  token: string | null | undefined
): Promise<AccidentAsset> => {
  const response = await fetch(
    `${apiVariable}/accident/preview?fileName=${encodeURIComponent(fileName)}`,
    {
      headers: {
        Authorization: `Bearer ${token ?? ""}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`File not found for fileName=${fileName}`);
  }

  const blob = await response.blob();
  const contentType = response.headers.get("Content-Type") ?? "";

  return {
    url: URL.createObjectURL(blob),
    contentType,
  };
};

const PreviewGalleryModal: React.FC<IPreviewGalleryModalProps> = ({
  open,
  onClose,
  accident,
}) => {
  const { token } = useAuth();
  const [assets, setAssets] = useState<Record<string, AccidentAsset>>({});
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetError, setAssetError] = useState<string | null>(null);
  const [activePreview, setActivePreview] = useState<{
    file: AccidentFile;
    asset: AccidentAsset;
  } | null>(null);

  const {
    data: files = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<AccidentFile[]>({
    queryKey: ["accidentImages", accident?.id],
    queryFn: async () => {
      const response = await axios.get(
        `${apiVariable}/accident/${accident.id}/images`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: open && !!accident?.id,
  });

  useEffect(() => {
    if (open && accident?.id) {
      refetch();
    }
  }, [open, accident?.id, refetch]);

  useEffect(() => {
    let isMounted = true;

    const loadAssets = async () => {
      if (!open) {
        return;
      }

      if (!files.length) {
        setAssets({});
        setAssetError(null);
        setLoadingAssets(false);
        return;
      }

      setLoadingAssets(true);
      setAssetError(null);

      let encounteredError = false;

      const results = await Promise.all(
        files.map(async (file) => {
          try {
            const asset = await loadAuthorizedFile(file.fileName, token);
            return { fileId: file.id, asset };
          } catch (error) {
            console.error("Error loading accident file:", error);
            encounteredError = true;
            return null;
          }
        })
      );

      if (!isMounted) {
        results.forEach((result) => {
          if (result) {
            URL.revokeObjectURL(result.asset.url);
          }
        });
        return;
      }

      const newAssets: Record<string, AccidentAsset> = {};
      results.forEach((result) => {
        if (result) {
          newAssets[result.fileId] = result.asset;
        }
      });

      setAssets(newAssets);
      setLoadingAssets(false);

      if (encounteredError) {
        setAssetError("Some files could not be loaded.");
      }
    };

    loadAssets();

    return () => {
      isMounted = false;
    };
  }, [open, files, token]);

  useEffect(() => {
    if (!open) {
      setAssets({});
      setAssetError(null);
      setActivePreview(null);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      Object.values(assets).forEach(({ url }) => URL.revokeObjectURL(url));
    };
  }, [assets]);

  const isPreviewable = (contentType: string) => {
    if (!contentType) {
      return false;
    }

    const lowerContentType = contentType.toLowerCase();
    return (
      lowerContentType.includes("pdf") ||
      lowerContentType.startsWith("image") ||
      lowerContentType.startsWith("video")
    );
  };

  const renderPreviewContent = (
    asset: AccidentAsset,
    fileName: string,
    variant: "compact" | "full" = "compact"
  ) => {
    const lowerContentType = asset.contentType.toLowerCase();
    const isPdf = lowerContentType.includes("pdf");
    const isVideo = lowerContentType.startsWith("video");
    const isImage = lowerContentType.startsWith("image");

    if (isPdf) {
      return (
        <Box
          component="iframe"
          src={asset.url}
          title={fileName}
          sx={{
            border: 0,
            width: "100%",
            minHeight: variant === "compact" ? 320 : "75vh",
          }}
        />
      );
    }

    if (isVideo) {
      return (
        <Box
          component="video"
          src={asset.url}
          controls
          sx={{
            display: "block",
            width: "100%",
            maxHeight: variant === "compact" ? 320 : "80vh",
            backgroundColor: "black",
          }}
        />
      );
    }

    if (isImage) {
      return (
        <Box
          component="img"
          src={asset.url}
          alt={fileName}
          sx={{
            display: "block",
            width: "100%",
            height: variant === "compact" ? 320 : "auto",
            maxHeight: variant === "compact" ? 320 : "80vh",
            objectFit: "contain",
            backgroundColor: "background.default",
          }}
        />
      );
    }

    return (
      <Box sx={{ py: 2 }}>
        <Typography>Preview not available for this file type.</Typography>
      </Box>
    );
  };

  const handleClosePreview = () => {
    setActivePreview(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Files: Accident #{accident?.id}</DialogTitle>
        <DialogContent>
          {isLoading || loadingAssets ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Typography color="error" sx={{ py: 2 }}>
              Unable to load accident files.
            </Typography>
          ) : !files.length ? (
            <Typography sx={{ py: 2 }}>
              No files found for this accident.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={3} sx={{ py: 1 }}>
              {assetError && (
                <Typography color="error">{assetError}</Typography>
              )}
              {files.map((file) => {
                const asset = assets[file.id];
                const previewable = asset
                  ? isPreviewable(asset.contentType)
                  : false;

                return (
                  <Box
                    key={file.id}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                  >
                    <Typography variant="subtitle1">{file.fileName}</Typography>
                    {!asset ? (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          position: "relative",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          overflow: "hidden",
                          backgroundColor: "background.paper",
                        }}
                      >
                        {renderPreviewContent(asset, file.fileName)}
                        {previewable && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              display: "flex",
                            }}
                          >
                            <Tooltip title="Open larger view">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  setActivePreview({ file, asset })
                                }
                              >
                                <ZoomInIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    )}
                    {asset && (
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          component="a"
                          href={asset.url}
                          download={file.fileName}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            mb: 1
                          }}
                        >
                          Download
                        </Button>
                        <ConfirmActionButton
                          id={file.id}
                          entityType="AccidentFile"
                          actionType="delete"
                          entityLabel="Accident file"
                          queryKey="accidentImages"
                          buttonLabel="Delete"
                          buttonColor="error"
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!activePreview}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{activePreview?.file.fileName}</DialogTitle>
        <DialogContent dividers>
          {activePreview &&
            renderPreviewContent(
              activePreview.asset,
              activePreview.file.fileName,
              "full"
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PreviewGalleryModal;
